import React, { useMemo } from "react";

import Markdown, { type Components } from "react-markdown";
import remarkDirective from "remark-directive";
import remarkGfm from "remark-gfm";
import { twJoin } from "tailwind-merge";

import { remarkLinkDirective } from "src/base-components/MarkdownRenderer/remarkLinkDirective";
import { MermaidDiagram } from "src/base-components/MermaidDiagram/MermaidDiagram";

type MarkdownRendererVariant = "default" | "muted";

type MarkdownRendererProps = {
  content: string;
  dataLoc?: string;
  variant?: MarkdownRendererVariant;
};

const VARIANT_CLASSES: Record<MarkdownRendererVariant, string> = {
  default: "text-gray-800",
  muted: "text-gray-500",
} as const;

const remarkPlugins = [remarkGfm, remarkDirective, remarkLinkDirective];

const isMermaidBlock = (node: unknown): boolean => {
  if (!node || typeof node !== "object" || !("properties" in node))
    return false;
  const props = (node as { properties?: { className?: string[] } }).properties;
  return props?.className?.includes("language-mermaid") ?? false;
};

const createComponents = (dataLoc?: string): Components => ({
  code: ({ className, children }) => {
    if (className === "language-mermaid") {
      return <MermaidDiagram chart={String(children)} dataLoc={dataLoc} />;
    }

    return <code className={className}>{children}</code>;
  },
  pre: ({ node, children }) => {
    if (isMermaidBlock(node?.children[0])) {
      return <>{children}</>;
    }

    return <pre>{children}</pre>;
  },
});

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  dataLoc,
  variant = "default",
}) => {
  const components = useMemo(() => createComponents(dataLoc), [dataLoc]);

  return (
    <div
      className={twJoin(
        "tktl-markdown font-inter-normal-12px",
        VARIANT_CLASSES[variant],
      )}
      data-loc={dataLoc}
    >
      <Markdown components={components} remarkPlugins={remarkPlugins}>
        {content}
      </Markdown>
    </div>
  );
};
